import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { Lock, ArrowUpRight } from "lucide-react";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";

const TierContext = createContext(null);

const TIER_HIERARCHY = { essential: 0, growth: 1, pro: 2 };

const TIER_FEATURES = {
  essential: [
    "core_booking", "basic_crm", "standard_reminders", "smartpage_whitelabel",
    "basic_analytics", "online_payments", "qr_code", "staff_management",
    "services_management", "reviews",
  ],
  growth: [
    "core_booking", "basic_crm", "standard_reminders", "smartpage_whitelabel",
    "basic_analytics", "online_payments", "qr_code", "staff_management",
    "services_management", "reviews",
    "whatsapp_basic", "follow_ups", "ltv_analytics", "ai_suggestions",
  ],
  pro: [
    "core_booking", "basic_crm", "standard_reminders", "smartpage_whitelabel",
    "basic_analytics", "online_payments", "qr_code", "staff_management",
    "services_management", "reviews",
    "whatsapp_basic", "follow_ups", "ltv_analytics", "ai_suggestions",
    "whatsapp_campaigns", "whatsapp_winback", "crm_scoring_advanced",
    "revenue_dashboard_advanced", "ai_suggestions_advanced", "multi_location",
    "priority_support",
  ],
};

export const useTier = () => {
  const ctx = useContext(TierContext);
  if (!ctx) return { tier: "pro", features: TIER_FEATURES.pro, loading: true, hasFeature: () => true, tierAtLeast: () => true };
  return ctx;
};

export const TierProvider = ({ children, user }) => {
  const [tier, setTier] = useState("pro");
  const [features, setFeatures] = useState(TIER_FEATURES.pro);
  const [loading, setLoading] = useState(true);
  const [isTrial, setIsTrial] = useState(false);

  const fetchTier = useCallback(async () => {
    if (!user || user.role === "super_admin" || user.role === "client" || user.role === "sales") {
      setTier("pro");
      setFeatures(TIER_FEATURES.pro);
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/subscription/my-tier`, { withCredentials: true });
      const t = res.data.tier || "essential";
      setTier(t);
      setFeatures(TIER_FEATURES[t] || TIER_FEATURES.essential);
      setIsTrial(res.data.is_trial || false);
    } catch {
      setTier("essential");
      setFeatures(TIER_FEATURES.essential);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchTier(); }, [fetchTier]);

  const hasFeature = useCallback((feature) => features.includes(feature), [features]);

  const tierAtLeast = useCallback((minTier) => {
    return (TIER_HIERARCHY[tier] ?? -1) >= (TIER_HIERARCHY[minTier] ?? 99);
  }, [tier]);

  const requiredTierForFeature = useCallback((feature) => {
    for (const t of ["essential", "growth", "pro"]) {
      if (TIER_FEATURES[t].includes(feature)) return t;
    }
    return "pro";
  }, []);

  return (
    <TierContext.Provider value={{ tier, features, loading, hasFeature, tierAtLeast, isTrial, requiredTierForFeature }}>
      {children}
    </TierContext.Provider>
  );
};

export const FeatureGate = ({ feature, minTier, children, fallback, inline = false }) => {
  const { hasFeature, tierAtLeast, tier, requiredTierForFeature } = useTier();

  const allowed = feature ? hasFeature(feature) : minTier ? tierAtLeast(minTier) : true;
  if (allowed) return children;

  const needed = feature ? requiredTierForFeature(feature) : minTier || "growth";

  if (fallback) return fallback;

  if (inline) {
    return <UpgradeBadge requiredTier={needed} />;
  }

  return <UpgradeOverlay requiredTier={needed} currentTier={tier} />;
};

export const UpgradeBadge = ({ requiredTier }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate("/subscription-required")}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-medium hover:bg-amber-500/20 transition-colors cursor-pointer"
      data-testid={`upgrade-badge-${requiredTier}`}
    >
      <Lock className="w-3 h-3" />
      {requiredTier.charAt(0).toUpperCase() + requiredTier.slice(1)}
    </button>
  );
};

export const UpgradeOverlay = ({ requiredTier, currentTier }) => {
  const navigate = useNavigate();
  const tierLabel = requiredTier.charAt(0).toUpperCase() + requiredTier.slice(1);
  return (
    <div className="relative rounded-2xl border border-amber-500/20 bg-amber-500/5 p-8 text-center" data-testid={`upgrade-overlay-${requiredTier}`}>
      <div className="w-14 h-14 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
        <Lock className="w-7 h-7 text-amber-400" />
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-2">
        Funzionalità Piano {tierLabel}
      </h3>
      <p className="text-sm text-muted-foreground mb-5 max-w-sm mx-auto">
        Questa sezione richiede il piano <span className="font-semibold text-amber-400">{tierLabel}</span>.
        Effettua l'upgrade per sbloccare tutte le funzionalità avanzate.
      </p>
      <Button
        onClick={() => navigate("/subscription-required")}
        className="rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-6"
      >
        Passa a {tierLabel}
        <ArrowUpRight className="ml-1.5 w-4 h-4" />
      </Button>
    </div>
  );
};

export default FeatureGate;
